import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowRight, CheckCircle, ArrowLeft, Wrench, ShieldCheck, Home, Sparkles, Loader2, Building2, Thermometer, Users, Landmark } from 'lucide-react';
import { pmsFrom, pmsAuth } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SEOHead } from '@/components/seo/SEOHead';
import { FAQ } from '@/components/common/FAQ';
import { LeadForm } from '@/components/forms/LeadForm';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { ConfigurableLayout } from '@/components/common/ConfigurableLayout';
import { useSiteContent } from '@/context/SiteContentContext';
import { useAutoHeroImage } from '@/hooks/useAutoHeroImage';
import { usePageDesign } from '@/hooks/usePageDesign';
import { useIndustrySlug } from '@/hooks/useIndustrySlug';
import { useBrandTrustBadges } from '@/hooks/useBrandTrustBadges';
import { sanitizeHtml } from '@/lib/sanitize';
import heroImage from '@/assets/hero-trades.jpg';

const ServiceAreaPage: React.FC = () => {
  const params = useParams<{ slug?: string; a?: string; b?: string; c?: string }>();
  const slug = params.slug || params.c;
  const regionSlugParam = params.b; // from /:industry/:region/:location
  const industrySlug = useIndustrySlug();
  const siteContent = useSiteContent();

  const { data: location, isLoading, error } = useQuery({
    queryKey: ['service-location', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('service_locations')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: businessSettings } = useQuery({
    queryKey: ['business-settings-public'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, phone, email, industry, address_state, license_number, years_experience')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const { data: allLocations = [] } = useQuery({
    queryKey: ['service-locations-sidebar'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_locations')
        .select('name, slug, region, region_id')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });
      return data || [];
    },
  });

  const { data: allRegions = [] } = useQuery({
    queryKey: ['service-regions-slugs'],
    queryFn: async () => {
      const { data } = await pmsFrom('service_regions')
        .select('id, slug, name')
        .eq('is_active', true);
      return data || [];
    },
  });

  // Fetch service-location pages for this location to show linked services
  const locationId = location?.id;
  const { data: servicePages = [] } = useQuery({
    queryKey: ['services-in-location', locationId],
    queryFn: async () => {
      const { data } = await pmsFrom('service_location_pages')
        .select('slug, title, service_id, services!inner(name, slug, icon, category_id, service_categories!inner(slug, segment_id, service_segments!inner(slug)))')
        .eq('location_id', locationId!)
        .eq('is_published', true)
        .order('title');
      return data || [];
    },
    enabled: !!locationId,
  });

  const companyName = businessSettings?.company_name || 'Our Company';
  const effectivePhone = location?.phone || businessSettings?.phone || '';
  const effectiveEmail = location?.email || businessSettings?.email || '';
  const stateAbbr = businessSettings?.address_state || '';
  const rawIndustry = businessSettings?.industry || 'services';

  // Map industry slugs to proper display names
  const industryLabelMap: Record<string, string> = {
    hvac: 'HVAC, Heating, Air Conditioning',
    plumbing: 'Plumbing',
    electrical: 'Electrical',
    roofing: 'Roofing',
    painting: 'Painting',
    flooring: 'Flooring',
    windows_doors: 'Windows & Doors',
    garage_doors: 'Garage Doors',
    solar: 'Solar / Renewable Energy',
    refrigeration: 'Refrigeration',
    fencing: 'Fencing',
    concrete: 'Concrete / Masonry',
    insulation: 'Insulation',
    remodeling: 'Remodeling',
    landscaping: 'Landscaping',
    cleaning: 'Cleaning',
    pest_control: 'Pest Control',
    appliance_repair: 'Appliance Repair',
    pool_spa: 'Pool & Spa',
    moving: 'Moving & Storage',
    handyman: 'Handyman',
    locksmith: 'Locksmith',
    home_inspection: 'Home Inspection',
    chimney: 'Chimney',
    waterproofing: 'Waterproofing',
  };
  const formatIndustryName = (slug: string) =>
    industryLabelMap[slug.trim()] || slug.trim().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const industryList = rawIndustry.split(',').map(formatIndustryName);
  const industryDisplay = industryList.length > 1
    ? `${industryList.slice(0, -1).join(', ')} and ${industryList[industryList.length - 1]}`
    : industryList[0];
  const industry = rawIndustry;

  // Auto-generate hero image if none exists and setting is enabled
  const { isGenerating: isGeneratingHero, generate: generateHero } = useAutoHeroImage({
    entityType: 'location',
    entityId: location?.id,
    entityName: location?.name,
    currentImage: location?.featured_image,
    industry: rawIndustry,
    state: stateAbbr,
  });

  // Check if user is logged in (admin) to show generate button
  const { data: session } = useQuery({
    queryKey: ['auth-session-hero'],
    queryFn: async () => {
      return await pmsAuth.getSession();
    },
    staleTime: 1000 * 60 * 5,
  });

  // Page design config
  const { heroStyle, layoutVariant, showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection, showLeadForm, isLoading: designLoading } = usePageDesign('location');

  // Trust badges -- sourced from Brand Intelligence when available, fallback to businessSettings
  const trustBadges = useBrandTrustBadges(businessSettings);

  // Local intelligence -- community info for location pages
  const { data: localIntel } = useQuery({
    queryKey: ['local-intelligence'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('local_intelligence')
        .select('neighborhoods, landmarks, climate_notes, demographics_notes, common_home_types, avg_home_age, homeowner_percentage, local_events')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  if (isLoading || designLoading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-24 space-y-6">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !location) {
    return null;
  }

  const faq = Array.isArray(location.faq) ? location.faq : [];
  const servicesAvailable = Array.isArray(location.services_available) ? location.services_available : [];
  const locationDisplayName = stateAbbr ? `${location.name}, ${stateAbbr}` : location.name;

  // Group nearby locations by region
  const regions = (allLocations as any[]).reduce<Record<string, any[]>>((acc, loc) => {
    const region = loc.region || 'Other';
    if (!acc[region]) acc[region] = [];
    acc[region].push(loc);
    return acc;
  }, {});

  // Sidebar content
  const sidebarContent = (
    <>
      {/* CTA Card */}
      <div className="bg-primary text-primary-foreground rounded-xl p-6 space-y-4">
        <h3 className="font-heading text-xl font-bold">Need {industryDisplay}?</h3>
        <p className="text-primary-foreground/80 text-sm">
          {companyName} serves {location.name} and surrounding areas.
        </p>
        {effectivePhone && (
          <Button asChild variant="secondary" className="w-full gap-2">
            <a href={`tel:${effectivePhone.replace(/\D/g, '')}`}>
              <Phone className="h-4 w-4" />
              Call {effectivePhone}
            </a>
          </Button>
        )}
        {effectiveEmail && (
          <a
            href={`mailto:${effectiveEmail}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-primary-foreground/30 text-primary-foreground text-sm hover:bg-primary-foreground/10 transition-colors"
          >
            <Mail className="h-4 w-4" />
            {effectiveEmail}
          </a>
        )}
        <Button asChild variant="outline" className="w-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
          <Link to="/contact">Request Free Estimate</Link>
        </Button>
      </div>

      {/* Services Provided in This Location (from SEO matrix) */}
      {servicePages.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Our Services in {location.name}
          </h3>
          <ul className="space-y-2">
            {servicePages.map((sp: any) => {
              const seg = sp.services?.service_categories?.service_segments;
              const cat = sp.services?.service_categories;
              const svc = sp.services;
              const href = seg && cat && svc
                ? `/${seg.slug}/${cat.slug}/${svc.slug}/${slug}`
                : '#';
              return (
                <li key={sp.slug}>
                  <Link
                    to={href}
                    className="flex items-center gap-2 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowRight className="h-3 w-3 flex-shrink-0 text-primary" />
                    {sp.title || svc?.name || 'Service'}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Other Service Areas */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          All Service Areas
        </h3>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(regions).map(([region, locs]) => (
            <div key={region}>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">{region}</p>
              <ul className="space-y-1">
                {(locs as any[]).map((loc: any) => (
                  <li key={loc.slug}>
                    <Link
                      to={(() => { const r = allRegions.find(r => r.id === (loc as any).region_id); return r ? `/${industrySlug}/${r.slug}/${loc.slug}` : `/${industrySlug}/${loc.slug}`; })()}
                      className={`text-sm flex items-center gap-1.5 py-1 transition-colors ${
                        loc.slug === slug
                          ? 'text-primary font-semibold'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <MapPin className="h-3 w-3 flex-shrink-0" />
                      {loc.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Button asChild variant="ghost" size="sm" className="w-full mt-4">
          <Link to={`/${industrySlug}`} className="gap-1">
            <ArrowLeft className="h-3 w-3" />
            View All Areas
          </Link>
        </Button>
      </div>
    </>
  );

  // Main content
  const mainContent = (
    <>
      {location.content && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg max-w-none dark:prose-invert seo-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(location.content) }}
        />
      )}

      {/* Services Available (legacy list) */}
      {servicesAvailable.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h2 className="font-heading text-2xl font-bold mb-6">Services Available in {location.name}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {servicesAvailable.map((service: string, i: number) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
                <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium">{service}</span>
              </div>
            ))}
          </div>
        </motion.section>
      )}

      {/* Local Facts */}
      {localIntel && (localIntel.neighborhoods?.length || localIntel.landmarks?.length || localIntel.climate_notes || localIntel.demographics_notes || localIntel.common_home_types?.length) && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <h2 className="font-heading text-2xl font-bold mb-6">About {location.name}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {localIntel.climate_notes && (
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Thermometer className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-sm">Climate</h4>
                </div>
                <p className="text-sm text-muted-foreground">{localIntel.climate_notes}</p>
              </div>
            )}
            {localIntel.demographics_notes && (
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-sm">Community</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {localIntel.demographics_notes}
                  {localIntel.homeowner_percentage ? ` · ${localIntel.homeowner_percentage}% homeowners` : ''}
                </p>
              </div>
            )}
            {localIntel.common_home_types && localIntel.common_home_types.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-sm">Common Home Types</h4>
                </div>
                <p className="text-sm text-muted-foreground">{localIntel.common_home_types.join(', ')}{localIntel.avg_home_age ? ` · Avg home age: ${localIntel.avg_home_age}` : ''}</p>
              </div>
            )}
            {localIntel.landmarks && localIntel.landmarks.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Landmark className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-sm">Nearby Landmarks</h4>
                </div>
                <p className="text-sm text-muted-foreground">{localIntel.landmarks.join(', ')}</p>
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* FAQ */}
      {showFaqSection && faq.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h2 className="font-heading text-2xl font-bold mb-6">Frequently Asked Questions — {location.name}</h2>
          <FAQ faqs={faq as Array<{ question: string; answer: string }>} />
        </motion.section>
      )}

      {/* Contact Form */}
      {showLeadForm && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-xl border border-border p-8"
        >
          <h2 className="font-heading text-2xl font-bold mb-2">Get Service in {location.name}</h2>
          <p className="text-muted-foreground mb-6">Fill out the form below and we'll get back to you promptly.</p>
          <LeadForm source={`service-area-${location.slug}`} />
        </motion.section>
      )}
    </>
  );

  return (
    <>
      <SEOHead
        title={location.meta_title || `${industryDisplay} Services in ${locationDisplayName} | ${companyName}`}
        description={location.meta_description || `Professional ${industryDisplay.toLowerCase()} services in ${locationDisplayName}. ${companyName} proudly serves ${location.name} and surrounding areas.`}
      />

      <ConfigurableHero
        heroStyle={heroStyle}
        title={`${location.name}'s Trusted ${industryDisplay} Experts.`}
        subtitle={location.description || `${companyName} proudly serves ${location.name} with reliable, professional ${industryDisplay.toLowerCase()} services tailored to your home's unique needs.`}
        backgroundImage={location.featured_image || heroImage}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="h-4 w-4" /> },
          { label: 'Service Areas', href: `/${industrySlug}`, icon: <MapPin className="h-4 w-4" /> },
          { label: locationDisplayName },
        ]}
        showBreadcrumbs={showBreadcrumbs}
        trustBadges={trustBadges}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
        phone={effectivePhone}
      >
        {/* Admin: Generate Hero Image */}
        {session && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-4">
            <Button size="sm" variant="outline" className="gap-2 text-xs opacity-70 hover:opacity-100" onClick={generateHero} disabled={isGeneratingHero}>
              {isGeneratingHero ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {isGeneratingHero ? 'Generating...' : 'Generate Hero Image'}
            </Button>
          </motion.div>
        )}
      </ConfigurableHero>

      <ConfigurableLayout
        layout={layoutVariant}
        main={mainContent}
        sidebar={sidebarContent}
      />
    </>
  );
};

export default ServiceAreaPage;
